import { RBACAccessMapModel } from '../models/rbac-access-matrix';
import { selectUser } from '../store/reducers';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  AclOwnership,
  getRBACAccessMatrixByUserRole,
  resourceType,
  roleAccessTypes,
  separators,
} from '../constants';
import { UserModel } from '../models';

function checkEachAccessType(
  accessRole: string,
  accessList: RBACAccessMapModel,
) {
  return Object.keys(accessList).some((key) =>
    new RegExp(`[${accessRole}]`, 'i').test(accessList[key]),
  );
}

export function checkRBACAccess(
  user: UserModel,
  resource: resourceType,
  roleAccessType: string | string[] = roleAccessTypes.read,
): boolean {
  if (!roleAccessType || !roleAccessType.length) {
    return false;
  }

  const userRole = (user && user.role) || null;

  const matrix = getRBACAccessMatrixByUserRole(userRole);

  if (!matrix || !matrix[resource]) {
    return false;
  }

  const accessTypes: string[] =
    typeof roleAccessType === 'string' ? [roleAccessType] : roleAccessType;

  const accessList: RBACAccessMapModel = (
    typeof matrix[resource] === 'string'
      ? {
          [AclOwnership.Any]: matrix[resource],
        }
      : matrix[resource]
  ) as RBACAccessMapModel;

  return accessTypes.some((expression: string): boolean => {
    const items = expression.split(separators.twoPoints);

    if (items.length < 2) {
      const [accessRole] = items;

      return checkEachAccessType(accessRole, accessList);
    }

    const [accessType, accessRole] = items;

    return new RegExp(`[${accessRole}]`, 'i').test(accessList[accessType]);
  });
}

export function useCheckRBACAccess(
  resource: resourceType,
  roleAccessTypes: string | string[],
  user?: UserModel,
): boolean {
  const localUser = user || useSelector(selectUser);
  const userRole = (localUser && localUser.role) || null;

  return React.useMemo(
    (): boolean => checkRBACAccess(localUser, resource, roleAccessTypes),
    [userRole, resource, roleAccessTypes],
  );
}
